import React, { useState } from 'react';
import { Spinner, Flex, Grid } from '@theme-ui/components';
import ReCAPTCHA from 'react-google-recaptcha';
import queryString from 'query-string';
import PropType from 'prop-types';
import _ from 'lodash';
import { ActionBanner } from '../molecules';
import { validate, saveForm } from '@greythr/shared-utils/hsAPI';
import { getButtonId } from '@greythr/shared-utils/common';
import { validatePhone } from '@greythr/shared-utils/form-validator';
import { Button, InputBox } from '../atoms';
import { colors } from '../../styles/theme';

const SERVER_ERROR =
	'Sorry, some error seems to have occurred and we could not complete your request. Please try again.';

let recaptchaRef = React.createRef();

const Input = ({
	label,
	name,
	value,
	maxlength,
	error,
	onChange,
	...props
}) => (
	<InputBox
		label={label}
		isRequired={true}
		labelColor={colors.white}
		errorColor={colors.white}
		name={name}
		value={value}
		maxlength={maxlength}
		customStyles={{
		width: '28rem',
		'@media(max-width: 1130px)': {
			width: '18.75rem',
		},
		'@media(max-width: 700px)': {
			width: '16rem',
		}
	}}
		error={error}
		onChange={onChange}
		{...props}
	/>
);

const Form = ({
	formCode,
	pageCode,
	pageName,
	pageUrl,
	regionCode,
	searchParams,
	setHideForm,
	setIsSuccess,
}) => {
	const utm_source = searchParams['utm_source'];
	const utm_medium = searchParams['utm_medium'];
	const utm_campaign = searchParams['utm_campaign'];

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [mobileNumber, setMobileNumber] = useState('');
	const [company, setCompany] = useState('');

	const [error, setError] = useState({});
	const [processing, setProcessing] = useState(false);
	const phoneNoLength = regionCode === "ME" ? 15 : 10;

	const validateForm = () => {
		let error = {};
		if (email) {
			const emailErr = validate(email);
			emailErr && (error['email'] = emailErr);
		}
		if (regionCode === "IN") {
			const phoneErr = validatePhone(mobileNumber);
			phoneErr && (error["mobile"] = phoneErr);
		}
		return error;
	};

	const handleClick = async (e) => {
		e.preventDefault();
		setError({});
		setProcessing(true);

		const formErrors = validateForm();
		if (!_.isEmpty(formErrors)) {
			setError(formErrors);
			setProcessing(false);
			return;
		}

		if (recaptchaRef && recaptchaRef.current) {
			let captcha = '';
			recaptchaRef.current.reset();
			captcha = await recaptchaRef.current.executeAsync();
			const payload = {
				mat_full_name: name,
				mat_email: email,
				mat_phone: mobileNumber,
				mat_company: company,
				page_code: pageCode,
				page_name: pageName,
				page_url: pageUrl,
				form_code: formCode,
				invisible_recaptcha: true,
				captcha,
				utm_campaign,
				utm_medium,
				utm_source,
			};
			if (captcha) {
				saveForm(
					payload,
					(response) => {
						setProcessing(false);
						setHideForm(true);
						setIsSuccess(true);
					},
					(error) => {
						setProcessing(false);
						if (
							error.response &&
							error.response.status == 400 &&
							error.response.data.invalid_email
						) {
							setError((prev) => ({
								...prev,
								email: error.response.data.invalid_email,
							}));
						} else {
							setHideForm(true);
							setIsSuccess(false);
						}
					}
				);
			} else {
				setProcessing(false);
				setHideForm(true);
				setIsSuccess(false);
			}
		} else {
			setProcessing(false);
			setHideForm(true);
			setIsSuccess(false);
		}
	};

     const handleOnChange = (e) => {
			regionCode === "IN" 
				? setMobileNumber(e.target.value.replace(/\D/g, '')) 
				: setMobileNumber(e.target.value.replace(/[^0-9+ ]/g, ''))
		}

	return (
		<Flex
			className='glass50'
			sx={styles.container}
			as='form'
			onSubmit={handleClick}
		>
			<Grid
				sx={styles.subContainer}
				columns={[1, 1, 2, 2]}
			>
				<Input
					type='text'
					label='Full Name'
					value={name}
					name='name'
					maxlength={100}
					error={error?.name}
					onChange={(e) => setName(e.target.value)}
				/>
				<Input
					type='text'
					label='Mobile Number'
					value={mobileNumber}
					name='mobileNumber'
					maxlength={phoneNoLength}
					error={error?.mobile}
					onChange={handleOnChange}
				/>
				<Input
					label='Business Email'
					name='email'
					value={email}
					maxlength={100}
					error={error?.email}
					onChange={(e) => setEmail(e.target.value)}
				/>
				<Input
					type='text'
					label='Company Name'
					value={company}
					name='company'
					maxlength={200}
					error={error?.company}
					onChange={(e) => setCompany(e.target.value)}
				/>
			</Grid>
			
			{processing ? (
				<Spinner
					color='white'
					size={58}
				/>
			) : (
				<Button
					id={getButtonId(`${pageCode}_request_demo`, regionCode)}
					variant='outlined'
					size='large'
					disabled={!email || !name || !mobileNumber || !company}
				>
					Request Demo
				</Button>
			)}
		</Flex>
	);
};

const defaultTitle = <>
Discuss business needs, find out pricing or get a demo.
<br />
We would love to talk.
</>

const PrimaryDemoForm = ({ formCode, pageCode, pageName, pageUrl, regionCode, search, title = defaultTitle }) => {
	const searchParams = queryString.parse(search);
	const [hideForm, setHideForm] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);

	const refresh = () => {
		setHideForm(false);
		setIsSuccess(false);
		navigate('#');
	};

	return (
		<Flex id='cta-block'>
			{hideForm ? (
				<ActionBanner
					theme='primary'
					variant='BodyBannerV1'
					title={
						isSuccess ? (
							<>
								Thank you for signing up for a demo.
								<br />
								You will hear from us soon.
							</>
						) : (
							SERVER_ERROR
						)
					}
					subTitle=''
					customStyles={{ minHeight: '44.5rem',width:"100%" }}
				>
					{!isSuccess && (
						<Button
							variant='outlined'
							size='large'
							onClick={refresh}
						>
							Refresh
						</Button>
					)}
				</ActionBanner>
			) : (
				<ActionBanner
					theme='primary'
					variant='BodyBannerV1'
					title={title}
					subTitle=''
					customStyles={{width:"100%"}}
				>
					<Form
						formCode={formCode}
						pageUrl={pageUrl}
						setHideForm={setHideForm}
						setIsSuccess={setIsSuccess}
						pageCode={pageCode}
						pageName={pageName}
						regionCode={regionCode}
						searchParams={searchParams}
					/>
				</ActionBanner>
			)}
			<ReCAPTCHA
				ref={recaptchaRef}
				size='invisible'
				sitekey={process.env.GATSBY_INVISIBLE_RE_CAPTCHA_SITE_KEY}
				badge='bottomleft'
			/>	
		</Flex>
	);
};

const styles = {
	container: {
		borderRadius: '16px',
		p: ['1.5rem', '1.5rem', '1.5rem', '2.5rem'],
		border: '1px solid rgba(232, 232, 233, 0.50)',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
	},
	subContainer: {
		columnGap: '1.5rem',
		rowGap: '1rem',
		px: [0, 0, 0, '2.5rem'],
		mb: '3rem',
	},
};

PrimaryDemoForm.propTypes = {
	pageCode: PropType.string,
	pageName: PropType.string,
	pageUrl: PropType.string,
	regionCode: PropType.string,
	search: PropType.any,
};

Form.propTypes = {
	pageCode: PropType.string,
	pageName: PropType.string,
	pageUrl: PropType.string,
	regionCode: PropType.string,
	searchParams: PropType.object,
	setHideForm: PropType.func,
	setIsSuccess: PropType.func,
};

Input.propTypes = {
	label: PropType.string,
	name: PropType.string,
	value: PropType.string,
	maxlength: PropType.number,
	error: PropType.string,
	onChange: PropType.func,
};

export default PrimaryDemoForm;
